import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import useGa4Property from "../hooks/useGa4Property"
import { useOrganization } from "../OrganizationContext"
import { supabase } from "../supabaseClient"
import { pageLocationHasPii } from "../utils/pageLocation"
import { computeGoogleAdsIssueCount } from "../utils/googleAdsAudit"
import {
  extractTemplateExpectedUsers,
  evaluateUserAccessIssues,
  normalizeUserAccessIdentifier,
} from "../utils/ga4UserAccess"

const SECTION_DEFINITIONS = [
  {
    id: "audits",
    label: "Audits",
    description: "Review property configuration and catch potential issues.",
    featureFlag: ({ hasPropertyId }) => hasPropertyId,
    items: [
      {
        id: "settings",
        label: "View Settings",
        description: "Inspect the GA4 property configuration and defaults.",
        destination: "/ga4-property/settings",
        featureFlag: ({ hasPropertyId, isAutomotive }) =>
          hasPropertyId && isAutomotive,
      },
      {
        id: "configuration-audit",
        label: "GA4 Configuration Audit",
        description:
          "Audit settings, data streams, integrations, events, parameters, and GTM coverage.",
        destination: "/ga4-property/configuration-audit",
        featureFlag: ({ hasPropertyId, isAutomotive }) =>
          hasPropertyId && !isAutomotive,
      },
      {
        id: "event-audit",
        label: "View Event Audit",
        description: "Validate tracked events and recommended conversions.",
        destination: "/ga4-property/event-audit",
        featureFlag: ({ hasPropertyId, ascEventUsageEnabled }) =>
          hasPropertyId && ascEventUsageEnabled,
      },
      {
        id: "page-location-audit",
        label: "PII In URL",
        description: "Check for personal data within captured page locations.",
        destination: "/ga4-property/page-location-audit",
        featureFlag: ({ hasPropertyId }) => hasPropertyId,
      },
      {
        id: "hostname-audit",
        label: "Hostname Audit",
        description: "Confirm traffic is attributed to the expected hostnames.",
        destination: "/ga4-property/hostname-audit",
        featureFlag: ({ hasPropertyId }) => hasPropertyId,
      },
      {
        id: "channel-grouping-audit",
        label: "Channel Grouping Audit",
        description: "Compare GA4 channel groupings with platform standards.",
        destination: "/ga4-property/channel-grouping-audit",
        featureFlag: ({ hasPropertyId }) => hasPropertyId,
      },
      {
        id: "unassigned-channel-group",
        label: "Unassigned Channel Group",
        description:
          "Review sources and mediums contributing to Unassigned sessions.",
        destination: "/ga4-property/unassigned-channel-group",
        featureFlag: ({ hasPropertyId }) => hasPropertyId,
      },
      {
        id: "self-referral-audit",
        label: "Self Referral Audit",
        description: "Find internal domains reported as referral traffic.",
        destination: "/ga4-property/self-referral-audit",
        featureFlag: ({ hasPropertyId }) => hasPropertyId,
      },
      {
        id: "google-ads-audit",
        label: "Google Ads Audit",
        description: "Review linked Google Ads accounts.",
        destination: "/ga4-property/google-ads-audit",
        featureFlag: ({ hasPropertyId }) => hasPropertyId,
      },
      {
        id: "user-access",
        label: "User Access",
        description: "Review GA4 user permissions and expected access.",
        destination: "/ga4-property/user-access",
        featureFlag: ({ hasPropertyId }) => hasPropertyId,
      },
    ],
  },
  {
    id: "performance",
    label: "Performance",
    description: "Dig into follow-up and close-conversion reporting.",
    featureFlag: ({ hasPropertyId, isAutomotive }) =>
      hasPropertyId && isAutomotive,
    items: [
      {
        id: "fcr",
        label: "View FCR",
        description: "Evaluate first-call resolution performance.",
        destination: "/ga4-property/fcr",
        featureFlag: ({ hasPropertyId }) => hasPropertyId,
      },
      {
        id: "ccr",
        label: "View CCR",
        description: "Review close-conversion results across products.",
        destination: "/ga4-property/ccr",
        featureFlag: ({ hasPropertyId }) => hasPropertyId,
      },
    ],
  },
]

const AUDIT_COUNT_ITEM_IDS = new Set([
  "settings",
  "configuration-audit",
  "event-audit",
  "page-location-audit",
  "hostname-audit",
  "channel-grouping-audit",
  "unassigned-channel-group",
  "self-referral-audit",
  "google-ads-audit",
  "user-access",
])

export default function GA4Property() {
  const navigate = useNavigate()
  const organization = useOrganization()
  const { search } = useLocation()
  const propertyId = new URLSearchParams(search).get("id")
  const { property: propInfo, loading, error } = useGa4Property(propertyId)
  const [auditCounts, setAuditCounts] = useState({})
  const [countsLoading, setCountsLoading] = useState(false)
  const [countsError, setCountsError] = useState(null)

  const hasPropertyId = Boolean(propertyId)
  const sectionContext = {
    hasPropertyId,
    property: propInfo,
    propertyId,
    isAutomotive: organization.isAutomotive !== false,
    ascEventUsageEnabled:
      organization.isAutomotive !== false &&
      organization.ascEventUsageEnabled !== false,
  }

  useEffect(() => {
    if (!propInfo?.id) {
      setAuditCounts({})
      setCountsLoading(false)
      setCountsError(null)
      return
    }

    let cancelled = false
    const propertyUuid = propInfo.id

    const fetchSettingsIssues = async () => {
      const { data, error: settingsError } = await supabase
        .from("ga4_property_settings")
        .select("issue_count, created_at")
        .eq("ga4_property_id", propertyUuid)
      if (settingsError) throw settingsError
      const rows = Array.isArray(data)
        ? data
        : data
        ? [data]
        : []
      let latestTimestamp = Number.NEGATIVE_INFINITY
      let latestCount = 0
      let fallbackCount = null
      for (const row of rows) {
        const count =
          typeof row?.issue_count === "number" ? row.issue_count : null
        if (count === null) continue
        const createdAt = row?.created_at ? Date.parse(row.created_at) : NaN
        if (!Number.isNaN(createdAt)) {
          if (createdAt > latestTimestamp) {
            latestTimestamp = createdAt
            latestCount = count
          }
        } else if (fallbackCount === null) {
          fallbackCount = count
        }
      }
      if (latestTimestamp > Number.NEGATIVE_INFINITY) return latestCount
      if (fallbackCount !== null) return fallbackCount
      return 0
    }

    const fetchEventIssues = async () => {
      const [eventResult, auditLogResult] = await Promise.all([
        supabase
          .from("ga4_event_audit_results")
          .select("event_owner, first_detected, expected")
          .eq("ga4_property_id", propertyUuid),
        supabase
          .from("ga4_event_owner_audits")
          .select("audited_at")
          .eq("ga4_property_id", propertyUuid),
      ])
      const { data: eventRows, error: eventError } = eventResult
      if (eventError) throw eventError
      const { data: auditLogRows, error: auditLogError } = auditLogResult
      if (auditLogError) throw auditLogError

      const lastAuditTimestamp = (() => {
        if (!Array.isArray(auditLogRows) || auditLogRows.length === 0) {
          return null
        }
        let latest = null
        for (const row of auditLogRows) {
          const raw = row?.audited_at
          if (!raw || typeof raw !== "string") continue
          const parsed = Date.parse(raw)
          if (Number.isNaN(parsed)) continue
          if (latest === null || parsed > latest) {
            latest = parsed
          }
        }
        return latest
      })()

      const filteredEventRows = Array.isArray(eventRows)
        ? eventRows.filter((row) => row?.expected !== true)
        : []

      const ownerFirstDetected = new Map()
      for (const row of filteredEventRows) {
        if (!row || typeof row !== "object") continue
        const owner =
          typeof row.event_owner === "string" ? row.event_owner : ""
        if (!owner) continue
        const rawDate =
          typeof row.first_detected === "string" ? row.first_detected : null
        const parsedDate = rawDate
          ? Date.parse(`${rawDate}T00:00:00Z`)
          : null
        const existing = ownerFirstDetected.get(owner)
        if (existing === undefined) {
          ownerFirstDetected.set(
            owner,
            Number.isNaN(parsedDate) ? null : parsedDate,
          )
          continue
        }
        const normalizedParsed = Number.isNaN(parsedDate) ? null : parsedDate
        if (existing === null && normalizedParsed !== null) {
          ownerFirstDetected.set(owner, normalizedParsed)
          continue
        }
        if (
          typeof existing === "number" &&
          typeof normalizedParsed === "number" &&
          normalizedParsed < existing
        ) {
          ownerFirstDetected.set(owner, normalizedParsed)
        }
      }

      if (!ownerFirstDetected.size) {
        return filteredEventRows.length
      }

      if (lastAuditTimestamp === null) {
        return ownerFirstDetected.size
      }

      let issues = 0
      for (const firstDetected of ownerFirstDetected.values()) {
        if (firstDetected === null || firstDetected > lastAuditTimestamp) {
          issues += 1
        }
      }
      return issues
    }

    const fetchPageLocationIssues = async () => {
      const { data, error: pageError } = await supabase
        .from("ga4_page_location_audit_results")
        .select("page_location, pii_matches")
        .eq("ga4_property_id", propertyUuid)
      if (pageError) throw pageError
      const rows = Array.isArray(data) ? data : []
      return rows
        .map((row) => ({
          page_location:
            typeof row?.page_location === "string" ? row.page_location : "",
          pii_matches: Array.isArray(row?.pii_matches) ? row.pii_matches : [],
        }))
        .filter((row) => pageLocationHasPii(row.page_location, row.pii_matches))
        .length
    }

    const fetchHostnameIssues = async () => {
      const { data, error: hostnameError } = await supabase
        .from("ga4_hostname_audit_results")
        .select("expected, last_detected")
        .eq("ga4_property_id", propertyUuid)
      if (hostnameError) throw hostnameError
      const rows = Array.isArray(data) ? data : []
      return rows.filter((row) => {
        const expected = row?.expected === true
        const lastDetected = row?.last_detected ? String(row.last_detected) : null
        return !expected && !lastDetected
      }).length
    }

    const fetchChannelGroupingIssues = async () => {
      const [channelResult, templateResult] = await Promise.all([
        supabase
          .from("ga4_channel_grouping_audit_results")
          .select("id", { count: "exact", head: true })
          .eq("ga4_property_id", propertyUuid),
        supabase
          .from("ga4_channel_group_template_audit_results")
          .select("id", { count: "exact", head: true })
          .eq("ga4_property_id", propertyUuid),
      ])
      if (channelResult.error) throw channelResult.error
      if (templateResult.error) throw templateResult.error
      const dataCount =
        typeof channelResult.count === "number" ? channelResult.count : 0
      const templateCount =
        typeof templateResult.count === "number" ? templateResult.count : 0
      return dataCount + templateCount
    }

    const fetchUnassignedChannelGroupIssues = async () => {
      const { count, error: unassignedError } = await supabase
        .from("ga4_channel_grouping_audit_results")
        .select("id", { count: "exact", head: true })
        .eq("ga4_property_id", propertyUuid)
        .ilike("channel_group", "Unassigned")
      if (unassignedError) throw unassignedError
      return typeof count === "number" ? count : 0
    }

    const fetchSelfReferralIssues = async () => {
      const { count, error: selfReferralError } = await supabase
        .from("ga4_self_referral_audit_results")
        .select("id", { count: "exact", head: true })
        .eq("ga4_property_id", propertyUuid)
      if (selfReferralError) throw selfReferralError
      return typeof count === "number" ? count : 0
    }

    const fetchGoogleAdsIssues = async () => {
      if (propInfo?.does_not_use_google_ads) {
        return 0
      }
      const [linkResult, costResult] = await Promise.all([
        supabase
          .from("ga4_google_ads_audit_results")
          .select("customer_id")
          .eq("ga4_property_id", propertyUuid),
        supabase
          .from("ga4_google_ads_cost_audit_results")
          .select(
            "google_ads_customer_id, advertiser_ad_cost",
          )
          .eq("ga4_property_id", propertyUuid),
      ])

      const { data: linkRows, error: linkError } = linkResult
      if (linkError) throw linkError
      const { data: costRows, error: costError } = costResult
      if (costError) throw costError

      return computeGoogleAdsIssueCount(linkRows, costRows)
    }

    const fetchUserAccessIssues = async () => {
      if (!propertyUuid) return 0
      const [rowsResult, stateResult, confirmationResult] = await Promise.all([
        supabase
          .from("ga4_user_access_audit_results")
          .select(
            "identifier, normalized_identifier, principal_email, user_link_name, direct_roles, effective_roles, access_level",
          )
          .eq("ga4_property_id", propertyUuid),
        supabase
          .from("ga4_user_access_audit_states")
          .select("audit_status")
          .eq("ga4_property_id", propertyUuid)
          .maybeSingle(),
        supabase
          .from("ga4_user_access_confirmations")
          .select("confirmed_identifiers")
          .eq("ga4_property_id", propertyUuid)
          .maybeSingle(),
      ])

      if (rowsResult.error) throw rowsResult.error
      const rows = Array.isArray(rowsResult.data) ? rowsResult.data : []
      const statusRecord = stateResult.error ? null : stateResult.data
      const status =
        (statusRecord && typeof statusRecord.audit_status === "string"
          ? statusRecord.audit_status
          : "ok") || "ok"
      const confirmationIdentifiers = Array.isArray(
        confirmationResult.data?.confirmed_identifiers,
      )
        ? confirmationResult.data.confirmed_identifiers
        : []

      let expectedUsers = []
      if (propInfo?.template_id) {
        const { data: templateData, error: templateError } = await supabase
          .from("ga4_templates")
          .select("template")
          .eq("id", propInfo.template_id)
          .maybeSingle()
        if (!templateError) {
          expectedUsers = extractTemplateExpectedUsers(
            templateData?.template,
          )
        } else {
          console.error("Failed to load template for user access issues", templateError)
        }
      }

      const normalizedRows = rows.map((row) => {
        const identifier = row?.identifier || null
        const rawUserLinkName = row?.user_link_name || null
        const normalizedIdentifier =
          row?.normalized_identifier ||
          normalizeUserAccessIdentifier(identifier) ||
          normalizeUserAccessIdentifier(rawUserLinkName) ||
          normalizeUserAccessIdentifier(row?.principal_email) ||
          null
        return {
          identifier,
          normalizedIdentifier,
          principalEmail: row?.principal_email || null,
          userLinkName:
            normalizedIdentifier || (rawUserLinkName ? String(rawUserLinkName) : null),
          directRoles: Array.isArray(row?.direct_roles)
            ? row.direct_roles
            : [],
          effectiveRoles: Array.isArray(row?.effective_roles)
            ? row.effective_roles
            : [],
          accessLevel:
            typeof row?.access_level === "string" && row.access_level
              ? row.access_level
              : null,
        }
      })

      const evaluation = evaluateUserAccessIssues({
        rows: normalizedRows,
        status,
        confirmationIdentifiers,
        expectedUsers,
      })
      return evaluation.issueCount
    }

    const loadCounts = async () => {
      setCountsLoading(true)
      setCountsError(null)
      try {
        const [
          settingsIssues,
          eventIssues,
          pageLocationIssues,
          hostnameIssues,
          channelIssues,
          unassignedChannelIssues,
          selfReferralIssues,
          googleAdsIssues,
          userAccessIssues,
        ] = await Promise.all([
          fetchSettingsIssues(),
          fetchEventIssues(),
          fetchPageLocationIssues(),
          fetchHostnameIssues(),
          fetchChannelGroupingIssues(),
          fetchUnassignedChannelGroupIssues(),
          fetchSelfReferralIssues(),
          fetchGoogleAdsIssues(),
          fetchUserAccessIssues(),
        ])
        if (cancelled) return
        setAuditCounts({
          settings: settingsIssues,
          "configuration-audit": settingsIssues,
          "event-audit": eventIssues,
          "page-location-audit": pageLocationIssues,
          "hostname-audit": hostnameIssues,
          "channel-grouping-audit": channelIssues,
          "unassigned-channel-group": unassignedChannelIssues,
          "self-referral-audit": selfReferralIssues,
          "google-ads-audit": googleAdsIssues,
          "user-access": userAccessIssues,
        })
        setCountsError(null)
      } catch (err) {
        if (cancelled) return
        console.error("Failed to load GA4 property audit counts", err)
        setAuditCounts({})
        setCountsError(err)
      } finally {
        if (!cancelled) setCountsLoading(false)
      }
    }

    loadCounts()

    return () => {
      cancelled = true
    }
  }, [propInfo?.id, propInfo?.does_not_use_google_ads, propInfo?.template_id])

  const availableSections = SECTION_DEFINITIONS.map((section) => {
    const sectionEnabled =
      section.featureFlag === undefined
        ? true
        : typeof section.featureFlag === "function"
        ? section.featureFlag(sectionContext)
        : !!section.featureFlag

    if (!sectionEnabled) return null

    const items = section.items.filter((item) => {
      if (item.featureFlag === undefined) return true
      if (typeof item.featureFlag === "function") return item.featureFlag(sectionContext)
      return !!item.featureFlag
    })

    if (items.length === 0) return null

    return {
      ...section,
      items,
    }
  }).filter(Boolean)

  const handleNavigate = (destination) => {
    if (!hasPropertyId) return
    navigate(`${destination}?id=${encodeURIComponent(propertyId)}`)
  }

  return (
    <div className="page-container ga4-property">
      <div className="ga4-property__header">
        <h2 className="ga4-property__title">View Property</h2>
        <button
          type="button"
          className="button--secondary"
          onClick={() => navigate("/ga4-audit")}
        >
          Back
        </button>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : error ? (
        <p style={{ marginTop: "1rem", color: "#b00020" }}>
          Failed to load property details.
        </p>
      ) : (
        <>
          {propInfo ? (
            <div className="ga4-property__summary">
              <h3 className="ga4-property__name">
                {propInfo.property_name}
                <span className="ga4-property__id">({propInfo.property_id})</span>
              </h3>
            </div>
          ) : (
            <p className="ga4-property__empty">Property not found.</p>
          )}
          {availableSections.length > 0 && (
            <div className="ga4-property-sections">
              {availableSections.map((section) => (
                <section key={section.id} className="ga4-property-section">
                  <header className="ga4-property-section__header">
                    <h3 className="ga4-property-section__title">{section.label}</h3>
                    <p className="ga4-property-section__description">
                      {section.description}
                    </p>
                  </header>
                  <ul className="ga4-property-section__actions">
                    {section.items.map((item) => (
                      <li key={item.id} className="ga4-property-section__action">
                        <button
                          type="button"
                          className="ga4-property-section__action-button"
                          onClick={() => handleNavigate(item.destination)}
                          aria-label={(() => {
                            const hasCount = AUDIT_COUNT_ITEM_IDS.has(item.id)
                            const itemCount = auditCounts[item.id]
                            if (
                              !hasCount ||
                              typeof itemCount !== "number" ||
                              Number.isNaN(itemCount)
                            )
                              return item.label
                            const issueText =
                              itemCount === 1 ? "issue" : "issues"
                            return `${item.label} (${itemCount} ${issueText})`
                          })()}
                        >
                          <span className="ga4-property-section__action-label">
                            {item.label}
                          </span>
                          {AUDIT_COUNT_ITEM_IDS.has(item.id) && (
                            countsLoading ? (
                              <span
                                className="ga4-property-section__count ga4-property-section__count--loading"
                                aria-hidden="true"
                              >
                                …
                              </span>
                            ) : typeof auditCounts[item.id] === "number" &&
                              !Number.isNaN(auditCounts[item.id]) ? (
                              <span
                                className={`ga4-property-section__count ${
                                  auditCounts[item.id] > 0
                                    ? "ga4-property-section__count--has-issues"
                                    : "ga4-property-section__count--no-issues"
                                }`}
                                aria-hidden="true"
                              >
                                {auditCounts[item.id]}
                              </span>
                            ) : null
                          )}
                        </button>
                        {item.description && (
                          <p className="ga4-property-section__action-description">
                            {item.description}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}
          {countsError && (
            <p className="ga4-property__counts-error">
              Failed to load audit issue counts.
            </p>
          )}
        </>
      )}
    </div>
  )
}
